import type { ConsentData } from '@/core/types';

export class ConsentStorage {
  private static readonly COOKIE_NAME = '__cmp_consent';
  private static readonly LOCALSTORAGE_KEY = '__cmp_consent_data';
  private static readonly VERSION_KEY = '__cmp_version';
  private static readonly CURRENT_VERSION = '1.0';

  private projectId: string;
  private cookieExpiry: number;

  constructor(projectId: string, cookieExpiry = 365) {
    this.projectId = projectId;
    this.cookieExpiry = cookieExpiry;
  }

  public saveConsent(consent: ConsentData): void {
    const consentWithMeta = {
      ...consent,
      projectId: this.projectId,
      version: ConsentStorage.CURRENT_VERSION,
      timestamp: Date.now(),
    };

    this.setCookie(ConsentStorage.COOKIE_NAME, JSON.stringify(consentWithMeta));
    this.setLocalStorage(ConsentStorage.LOCALSTORAGE_KEY, consentWithMeta);
    this.setLocalStorage(
      ConsentStorage.VERSION_KEY,
      ConsentStorage.CURRENT_VERSION
    );
  }

  public getConsent(): ConsentData | null {
    let consent: ConsentData | null = null;

    try {
      const cookieData = this.getCookie(ConsentStorage.COOKIE_NAME);
      if (cookieData) {
        consent = JSON.parse(cookieData);
      } else {
        const localData = this.getLocalStorage(ConsentStorage.LOCALSTORAGE_KEY);
        if (localData) {
          consent = localData;
        }
      }

      if (consent && this.isValidConsent(consent)) {
        if (this.needsVersionUpdate(consent)) {
          return null;
        }
        return consent;
      }
    } catch (error) {
      console.warn(
        'ConsentManager: Failed to parse stored consent data',
        error
      );
      this.clearConsent();
    }

    return null;
  }

  public clearConsent(): void {
    this.deleteCookie(ConsentStorage.COOKIE_NAME);
    this.removeLocalStorage(ConsentStorage.LOCALSTORAGE_KEY);
    this.removeLocalStorage(ConsentStorage.VERSION_KEY);
  }

  public hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  public getConsentAge(): number | null {
    const consent = this.getConsent();
    if (!consent) return null;
    return Date.now() - consent.timestamp;
  }

  public isConsentExpired(maxAge: number = 365 * 24 * 60 * 60 * 1000): boolean {
    const age = this.getConsentAge();
    return age !== null && age > maxAge;
  }

  private isValidConsent(consent: any): consent is ConsentData {
    return (
      consent &&
      typeof consent === 'object' &&
      typeof consent.essential === 'boolean' &&
      typeof consent.analytics === 'boolean' &&
      typeof consent.advertising === 'boolean' &&
      typeof consent.functional === 'boolean' &&
      typeof consent.personalization === 'boolean' &&
      typeof consent.timestamp === 'number' &&
      typeof consent.version === 'string'
    );
  }

  private needsVersionUpdate(consent: ConsentData): boolean {
    return consent.version !== ConsentStorage.CURRENT_VERSION;
  }

  private setCookie(name: string, value: string): void {
    try {
      const expires = new Date();
      expires.setTime(
        expires.getTime() + this.cookieExpiry * 24 * 60 * 60 * 1000
      );

      const cookieOptions = [
        `${name}=${encodeURIComponent(value)}`,
        `expires=${expires.toUTCString()}`,
        'path=/',
        'SameSite=Lax',
      ];

      if (location.protocol === 'https:') {
        cookieOptions.push('Secure');
      }

      document.cookie = cookieOptions.join('; ');
    } catch (error) {
      console.warn('ConsentManager: Failed to set cookie', error);
    }
  }

  private getCookie(name: string): string | null {
    try {
      const nameEQ = name + '=';
      const ca = document.cookie.split(';');

      for (let c of ca) {
        c = c.trim();
        if (c.indexOf(nameEQ) === 0) {
          return decodeURIComponent(c.substring(nameEQ.length));
        }
      }
    } catch (error) {
      console.warn('ConsentManager: Failed to read cookie', error);
    }
    return null;
  }

  private deleteCookie(name: string): void {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    } catch (error) {
      console.warn('ConsentManager: Failed to delete cookie', error);
    }
  }

  private setLocalStorage(key: string, value: any): void {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.setItem(`${this.projectId}_${key}`, JSON.stringify(value));
      }
    } catch (error) {
      console.warn('ConsentManager: Failed to set localStorage', error);
    }
  }

  private getLocalStorage(key: string): any | null {
    try {
      if (this.isLocalStorageAvailable()) {
        const item = localStorage.getItem(`${this.projectId}_${key}`);
        return item ? JSON.parse(item) : null;
      }
    } catch (error) {
      console.warn('ConsentManager: Failed to read localStorage', error);
    }
    return null;
  }

  private removeLocalStorage(key: string): void {
    try {
      if (this.isLocalStorageAvailable()) {
        localStorage.removeItem(`${this.projectId}_${key}`);
      }
    } catch (error) {
      console.warn('ConsentManager: Failed to remove localStorage', error);
    }
  }

  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorage_test__';
      localStorage.setItem(test, 'test');
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  public migrateFromLegacyStorage(
    legacyCookieName?: string
  ): ConsentData | null {
    if (!legacyCookieName) return null;

    try {
      const legacyData = this.getCookie(legacyCookieName);
      if (legacyData) {
        const parsed = JSON.parse(legacyData);

        const migratedConsent: ConsentData = {
          essential: true,
          analytics: parsed.analytics || false,
          advertising: parsed.advertising || parsed.marketing || false,
          functional: parsed.functional || false,
          personalization:
            parsed.personalization || parsed.preferences || false,
          timestamp: parsed.timestamp || Date.now(),
          version: ConsentStorage.CURRENT_VERSION,
        };

        this.saveConsent(migratedConsent);
        this.deleteCookie(legacyCookieName);

        return migratedConsent;
      }
    } catch (error) {
      console.warn(
        'ConsentManager: Failed to migrate legacy consent data',
        error
      );
    }

    return null;
  }
}
